import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { CURRENCY } from '../lib/mockBank';
import { CreditCard, CheckCircle2, Loader2, Sparkles, ShieldCheck, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

/**
 * MockPaymentDialog — a FAKE checkout that charges the FULL price of one
 * question-bank folder. No real payment is processed. On success it calls
 * onConfirm() so the caller can mark the folder as purchased.
 */
const MockPaymentDialog = ({ open, onOpenChange, topicTitle = '', price = 0, onConfirm }) => {
  const [card, setCard] = useState({ number: '', name: '', exp: '', cvc: '' });
  const [processing, setProcessing] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) { setProcessing(false); setDone(false); }
  }, [open]);

  const reset = () => {
    setProcessing(false);
    setDone(false);
    setCard({ number: '', name: '', exp: '', cvc: '' });
  };

  const handleClose = (v) => {
    if (processing) return;
    if (!v) setTimeout(reset, 200);
    onOpenChange(v);
  };

  const handlePay = async () => {
    setProcessing(true);
    // Simulate a payment gateway round-trip — purely cosmetic.
    await new Promise((r) => setTimeout(r, 1300));
    setProcessing(false);
    setDone(true);
    onConfirm?.();
    toast.success(`تم فتح «${topicTitle}» بنجاح `);
    setTimeout(() => handleClose(false), 1400);
  };

  const formatCardNumber = (v) =>
    v.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#412402]">
            <CreditCard className="w-5 h-5 text-[#EF9F27]" />
            إتمام الدفع
          </DialogTitle>
          <DialogDescription className="flex items-center gap-1.5 text-xs">
            <Badge variant="secondary" className="bg-amber-100 text-amber-800 gap-1">
              <Sparkles className="w-3 h-3" /> وضع تجريبي
            </Badge>
            لا يتم خصم أي مبلغ حقيقي — هذه شاشة عرض فقط.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-10 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-[#412402] mb-1">تم الدفع بنجاح</h3>
            <p className="text-[#412402]/60 text-sm">
              تم فتح مجلد «{topicTitle}». يمكنك بدء التدرّب الآن.
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Order summary */}
            <div className="flex items-center justify-between bg-[#FFF8E7] border border-[#F0E2C0] rounded-xl px-4 py-3">
              <span className="text-sm text-[#412402]/70 flex items-center gap-1.5">
                <FolderOpen className="w-4 h-4 text-[#EF9F27]" />
                {topicTitle || 'مجلد بنك الأسئلة'}
              </span>
              <span className="font-bold text-[#412402] text-lg">{price} {CURRENCY}</span>
            </div>

            {/* Fake card form */}
            <div className="space-y-3">
              <Label className="text-[#412402] flex items-center gap-1.5">
                <CreditCard className="w-4 h-4 text-[#EF9F27]" />
                بيانات البطاقة (وهمية)
              </Label>
              <Input
                dir="ltr"
                placeholder="4242 4242 4242 4242"
                value={card.number}
                onChange={(e) => setCard({ ...card, number: formatCardNumber(e.target.value) })}
              />
              <Input
                placeholder="الاسم على البطاقة"
                value={card.name}
                onChange={(e) => setCard({ ...card, name: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  dir="ltr"
                  placeholder="MM/YY"
                  value={card.exp}
                  onChange={(e) => setCard({ ...card, exp: e.target.value.replace(/[^\d/]/g, '').slice(0, 5) })}
                />
                <Input
                  dir="ltr"
                  placeholder="CVC"
                  value={card.cvc}
                  onChange={(e) => setCard({ ...card, cvc: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                />
              </div>
            </div>

            <Button
              onClick={handlePay}
              disabled={processing}
              className="w-full bg-[#EF9F27] hover:bg-[#D88E1F] text-white h-11"
            >
              {processing ? (
                <><Loader2 className="w-4 h-4 ml-2 animate-spin" /> جارٍ المعالجة...</>
              ) : (
                <>ادفع {price} {CURRENCY} وافتح المجلد</>
              )}
            </Button>

            <p className="flex items-center justify-center gap-1.5 text-[11px] text-[#412402]/40">
              <ShieldCheck className="w-3.5 h-3.5" />
              عرض توضيحي — لا تُرسل أي بيانات بطاقة فعلية.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MockPaymentDialog;
